<script lang="ts">
import { computed, defineComponent, inject, ref } from "vue";
import formValidierung from "@/businesslogic/formValidierung";
import formatUtils from "@/businesslogic/utils/formatUtils";
import AnfrageService from "@/businesslogic/services/AnfrageService";
import Infotext from "@/components/Infotext.vue";
import Zelle from "@/components/grid/Zelle.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zeile from "@/components/grid/Zeile.vue";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import Modal from "@/components/Modal.vue";
import Logger from "js-logger";
import AbschlussMakler from "@/components/abschluss/AbschlussMakler.vue";
import AbschlussDirekt from "@/components/abschluss/AbschlussDirekt.vue";
import BuendelDokumentLinks from "@/components/vorschlag/modals/BuendelDokumentLinks.vue";
import beschriftungUtils from "@/businesslogic/utils/beschriftungUtils";
import AbschlussIhreDaten from "@/components/abschluss/AbschlussIhreDaten.vue";
import AbschlussHinweise from "@/components/abschluss/AbschlussHinweise.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import DateiUploadListe from "@/views/vgv/views/DateiUploadListe.vue";
import storeUtils from "@/businesslogic/utils/storeUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "Abschluss",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    DateiUploadListe,
    AbschlussHinweise,
    AbschlussIhreDaten,
    AbschlussDirekt,
    AbschlussMakler,
    Infotext,
    Zelle,
    TitelZelle,
    Zeile,
    Ueberschrift,
    Modal,
    BuendelDokumentLinks,
    ValidierungsFehler,
  },
  props: {
    zeigeVersichertePerson: {
      type: Boolean,
      default: false,
    },
    zeigeVersichertesObjekt: {
      type: Boolean,
      default: false,
    },
    zeigeTarif: {
      type: Boolean,
      default: false,
    },
    zeigeObjektAnschrift: {
      type: Boolean,
      default: false,
    },
    zeigeSelbstbeteiligung: {
      type: Boolean,
      default: true,
    },
    obligatorischeSelbstbeteiligung: {
      type: Number,
      default: undefined,
    },
    zeigeAnzahlVorschaeden: {
      type: Boolean,
      default: false,
    },
    zeigeEigentumsverhaeltnisse: {
      type: Boolean,
      default: false,
    },
    zeigeAngabenBestaetigen: {
      type: Boolean,
      default: true,
    },
    zusaetzlicheSbLw: {
      type: Boolean,
      default: false,
    },
    produkt: String,
    hinweistext: {
      type: String,
      default: "",
    },
  },
  setup(props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const zeigeSpinner: any = inject("zeigeSpinner");
    const fehler = ref({} as any);

    const { formatCurrency, getZahlungsweiseAlsText } = formatUtils();
    const { anredeText, deckungssummeText, tarifVarianteText } = beschriftungUtils();
    const { enthaeltVGV, istFhkVerkaufsprodukt, istVhvVerkaufsprodukt } = storeUtils();

    const propsTitelZelle = {
      breite: 3,
      class: "o-grid__cell--4",
      "ausrichtung-responsiv": false,
      "vertikal-zentrieren": false,
    };

    const propsZelle = {
      breite: 9,
      class: "o-grid__cell--8",
    };

    const getDeckungssumme = computed(() => {
      Logger.debug("Deckungssumme:", piniaStore.state.tarifinformationen.deckungssumme);
      return deckungssummeText(piniaStore.state.tarifinformationen.deckungssumme);
    });

    const getVersichertePersonen = computed(() => {
      return tarifVarianteText(piniaStore.state.tarifinformationen.tarifVariante);
    });

    const getSelbstbeteiligung = computed(() => {
      if (
        piniaStore.state.tarifinformationen.selbstbeteiligung &&
        piniaStore.state.tarifinformationen.selbstbeteiligung > 0
      ) {
        return piniaStore.state.tarifinformationen.selbstbeteiligung.toString() + " €";
      }
      return "keine";
    });

    const getZahlweiseText = computed(() => {
      if (piniaStore.state.preise.produktLinien[0].einmalig) {
        return "Einmalbetrag";
      }
      return getZahlungsweiseAlsText(piniaStore.state.tarifinformationen.zahlungsweise);
    });

    const getZahlartText = computed(() => {
      if (
        piniaStore.state.zahlungsdaten.zahlungsart &&
        piniaStore.state.zahlungsdaten.zahlungsart.toLowerCase() == "debit"
      ) {
        return "Lastschrift";
      }
      return "Rechnung";
    });

    const getBankverbindung = computed(() => {
      if (
        piniaStore.state.zahlungsdaten.zahlungsart &&
        piniaStore.state.zahlungsdaten.zahlungsart.toLowerCase() == "debit"
      ) {
        let kontoInhaber = "";
        if (
          piniaStore.state.zahlungsdaten.kontoinhaberPartnerRolle === "anderePerson" &&
          piniaStore.state.zahlungsdaten.kontoinhaberPartner !== undefined
        ) {
          kontoInhaber +=
            piniaStore.state.zahlungsdaten.kontoinhaberPartner.vorname +
            " " +
            piniaStore.state.zahlungsdaten.kontoinhaberPartner.nachname;
        }
        if (
          piniaStore.state.personendaten.verwalterAktiv === true &&
          piniaStore.state.zahlungsdaten.kontoinhaberPartnerRolle === "versicherungsnehmer"
        ) {
          if (piniaStore.state.personendaten.versicherungsnehmer.nachname != "") {
            kontoInhaber +=
              piniaStore.state.personendaten.versicherungsnehmer.vorname +
              " " +
              piniaStore.state.personendaten.versicherungsnehmer.nachname;
          } else if (piniaStore.state.personendaten.versicherungsnehmer.organisation != "") {
            kontoInhaber += piniaStore.state.personendaten.versicherungsnehmer.organisation;
          }
        }
        return (
          piniaStore.state.zahlungsdaten.iban +
          (kontoInhaber !== "" ? " (Inhaber: " + kontoInhaber + ")" : kontoInhaber)
        );
      }
      return "";
    });

    const isDunkelverarbeitung = computed(() => {
      Logger.debug("piniaStore.state.isDunkelverarbeitung", piniaStore.state.isDunkelverarbeitung);
      return piniaStore.state.isDunkelverarbeitung;
    });

    const getAnredeText = computed(() => {
      if (piniaStore.state.personendaten.versicherungsnehmer !== undefined) {
        return anredeText(piniaStore.state.personendaten.versicherungsnehmer.anrede);
      }
      return "";
    });

    const getTarifbezeichnung = computed(() => {
      if (istFhkVerkaufsprodukt(piniaStore.state)) {
        const produkte: string[] = [];
        if (
          piniaStore.state.ferienhaus.versicherungsumfang == "0001" ||
          piniaStore.state.ferienhaus.versicherungsumfang == "0002"
        ) {
          produkte.push("Wohngebäudeversicherung");
        }
        if (
          piniaStore.state.ferienhaus.versicherungsumfang == "0001" ||
          piniaStore.state.ferienhaus.versicherungsumfang == "0003"
        ) {
          produkte.push("Hausratversicherung");
        }
        if (piniaStore.state.tarifinformationen.hausundgrundbesitzerhaftpflicht) {
          produkte.push("Haus- und Grundbesitzerhaftpflicht");
        }
        return "Ferienhauskonzept (" + produkte.join(", ") + ")";
      }
      return piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinienIndex].nameProduktLinie;
    });

    const getSchadenfreiheitsrabatt = computed(() => {
      if (
        props.produkt == "vgv" ||
        istVhvVerkaufsprodukt(piniaStore.state) ||
        istFhkVerkaufsprodukt(piniaStore.state)
      ) {
        if (piniaStore.state.tarifinformationen.sfr && piniaStore.state.tarifinformationen.sfr !== 0) {
          let vorschaedenGesamt = piniaStore.state.tarifinformationen.vorschaedenAnzahl;
          if (istFhkVerkaufsprodukt(piniaStore.state)) {
            vorschaedenGesamt = getSfrFH();
          }
          return "Ja (" + vorschaedenGesamt + (vorschaedenGesamt == 1 ? " Vorschaden)" : " Vorschäden)");
        }
        return "Nein";
      }
      return "";
    });

    const getSfrFH = (): number => {
      let vorschaedenFH = 0;
      if (
        piniaStore.state.ferienhaus.versicherungsumfang == "0001" ||
        piniaStore.state.ferienhaus.versicherungsumfang == "0002"
      ) {
        vorschaedenFH += !isNaN(+piniaStore.state.ferienhaus.vgv.tarifinformationen.vorschaedenAnzahl)
          ? +piniaStore.state.ferienhaus.vgv.tarifinformationen.vorschaedenAnzahl
          : 0;
      }
      if (
        piniaStore.state.ferienhaus.versicherungsumfang == "0001" ||
        piniaStore.state.ferienhaus.versicherungsumfang == "0003"
      ) {
        vorschaedenFH += !isNaN(+piniaStore.state.ferienhaus.vhv.tarifinformationen.vorschaedenAnzahl)
          ? +piniaStore.state.ferienhaus.vhv.tarifinformationen.vorschaedenAnzahl
          : 0;
      }

      return vorschaedenFH;
    };

    const eigentumsverhaeltnisse = computed(() => {
      if (piniaStore.state.gebaeudedaten.alleineigentuemer === false) {
        switch (piniaStore.state.gebaeudedaten.miteigentuemerTyp) {
          case "EHE":
            return "Eigentümergemeinschaft mit Ehe-/Lebenspartner";
          case "WEG":
            return "Wohnungseigentümergemeinschaft(WEG)";
          case "ETG":
            return "Eigentümergemeinschaft(EG)";
        }
      }

      return "Antragsteller ist alleiniger Eigentümer";
    });

    const gesamtPreisFH = computed(() => {
      return piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinien.length - 1].gemZahlungsweise;
    });

    const eingabenSindValide = async (): Promise<boolean> => {
      const { validiereForm, getFormFehler } = formValidierung();
      await validiereForm("");
      fehler.value = getFormFehler();
      Logger.debug("Form Fehler Anzahl:", Object.keys(fehler.value).length, fehler.value);
      return Object.keys(fehler.value).length == 0;
    };

    const verarbeiteAntragsAntwort = (res: any) => {
      piniaStore.state.status = res.data.anfrage.status;
      piniaStore.state.antragId = res.data.kubusData?.antragId;
      if (res.data.anfrage.isDunkelverarbeitung !== undefined) {
        piniaStore.state.isDunkelverarbeitung = res.data.anfrage.isDunkelverarbeitung;
      }

      emit("next-page", {
        pageIndex: bestaetigungsIndex(props.produkt),
      });
    };

    const bestaetigungsIndex = (produkt: string | undefined) => {
      if (produkt === "vgv") {
        return 4;
      }
      if (istFhkVerkaufsprodukt(piniaStore.state)) {
        return 5;
      }
      return 3;
    };

    const getAntragOderVertrag = (grosschreibung = false) => {
      if (!piniaStore.state.isDunkelverarbeitung) {
        return grosschreibung ? "Antrag" : "antrag";
      }
      return grosschreibung ? "Vertrag" : "vertrag";
    };

    const antragAbsenden = () => {
      eingabenSindValide().then(() => {
        if (Object.keys(fehler.value).length == 0) {
          zeigeSpinner.value = true;
          piniaStore.state.korrespondenz.kopieAnMakler = false;
          AnfrageService.absenden(piniaStore.getSessionToken(piniaStore.state), piniaStore.state, "Abschluss")
            .then((res) => {
              Logger.debug("antragAbsenden(): OK. Ergebnis: ", res?.data);
              if (res?.data) {
                verarbeiteAntragsAntwort(res);
              }
            })
            .catch((error) => {
              Logger.debug("antragAbsenden(): Fehler.", error);
              wechsleSichtbarkeit("abschlussfehler");
            })
            .then(() => {
              zeigeSpinner.value = false;
            });
        }
      });
    };

    const sindVorherigeStepsValide = () => {
      if (props.produkt == "vgv") {
        return anschriftGesetzt();
      }
      return true;
    };

    const anschriftGesetzt = () => {
      if (
        !piniaStore.state.personendaten.versicherungsnehmer.adresseAbweichendVonRisikoadresse ||
        (piniaStore.state.personendaten.versicherungsnehmer.adresse.plz != "" &&
          piniaStore.state.personendaten.versicherungsnehmer.adresse.ort != "" &&
          piniaStore.state.personendaten.versicherungsnehmer.adresse.strasse != "" &&
          piniaStore.state.personendaten.versicherungsnehmer.adresse.hausnummer != "")
      ) {
        delete fehler.value.vorherigeSteps;
        return true;
      }
      fehler.value.vorherigeSteps = "Bitte füllen Sie alle Felder der Anschrift auf 'Ihre Daten' aus.";
      return false;
    };

    const vorschaedenAnzahl = computed(() => {
      switch (piniaStore.state.tarifinformationen.vorschaedenAnzahl) {
        case -1:
        case 0:
          return "keine";
        case 1:
          return "1 Vorschaden";
        default:
          return piniaStore.state.tarifinformationen.vorschaedenAnzahl + " Vorschäden";
      }
    });

    function herunterladenDirekt() {
      zeigeSpinner.value = true;
      AnfrageService.herunterladen(
        piniaStore.getSessionToken(piniaStore.state),
        piniaStore.state,
        true,
        "Abschluss",
        "abschluss",
      )
        .then((res) => {
          Logger.debug("res: " + res);
          if (res.status != 200) {
            throw new Error(res.statusText);
          }
          return res.data;
        })
        .then((data) => {
          Logger.debug("Response", data);
          piniaStore.state.id = data.id;
          piniaStore.state.anfrageNummer = data.anfrageNummer;
          piniaStore.state.erstelldatum = data.erstelldatum;
          const fileLink = document.createElement("a");
          fileLink.href = "data:application/pdf;base64," + data.korrespondenz.vorschlagDokument;
          fileLink.setAttribute("download", data.korrespondenz.dokumentName);
          document.body.appendChild(fileLink);

          fileLink.click();
          zeigeSpinner.value = false;
        })
        .catch((err) => {
          Logger.warn(err);
          zeigeSpinner.value = false;
        });
    }

    const vorschlagHerunterladen = () => {
      if (piniaStore.state.isMakler) {
        wechsleSichtbarkeit("modal-vorschlagMaklerDownload");
      } else {
        if (istFhkVerkaufsprodukt(piniaStore.state)) {
          wechsleSichtbarkeit("modal-ferienhausDownload");
        } else {
          herunterladenDirekt();
        }
      }
    };

    function geheZuBasisdaten() {
      emit("goto-page", { pageIndex: 0 });
    }

    const geheZuVergleich = () => {
      if (props.produkt == "vgv" || istFhkVerkaufsprodukt(piniaStore.state)) {
        emit("goto-page", { pageIndex: 2 });
      } else {
        emit("goto-page", { pageIndex: 1 });
      }
    };

    const geheZuAntragsdaten = () => {
      if (props.produkt == "vgv" || istFhkVerkaufsprodukt(piniaStore.state)) {
        emit("goto-page", { pageIndex: 3 });
      } else {
        emit("goto-page", { pageIndex: 2 });
      }
    };

    const zeigeDateien = computed(() => {
      return (
        piniaStore.state.isMakler &&
        (piniaStore.state.verkaufsprodukt === "VGV" ||
          (istFhkVerkaufsprodukt(piniaStore.state) && enthaeltVGV(piniaStore.state)))
      );
    });

    return {
      props,
      piniaStore,
      fehler,
      getAntragOderVertrag,
      antragAbsenden,
      geheZuBasisdaten,
      geheZuVergleich,
      geheZuAntragsdaten,
      getTarifbezeichnung,
      getSchadenfreiheitsrabatt,
      getVersichertePersonen,
      getSelbstbeteiligung,
      getZahlweiseText,
      getZahlartText,
      getBankverbindung,
      isDunkelverarbeitung,
      getAnredeText,
      formatCurrency,
      wechsleSichtbarkeit,
      istSichtbar,
      vorschlagHerunterladen,
      propsTitelZelle,
      propsZelle,
      vorschaedenAnzahl,
      eigentumsverhaeltnisse,
      getDeckungssumme,
      gesamtPreisFH,
      sindVorherigeStepsValide,
      zeigeDateien,
      istFhkVerkaufsprodukt,
    };
  },
});
</script>

<template>
  <section>
    <ueberschrift
      :titel="(piniaStore.state.isMakler === true ? '' : 'Ihr ') + 'Versicherungs&shy;' + getAntragOderVertrag(false)"
      :ausrichtung-responsiv="false"
      :vertikal-zentrieren="false"
    />

    <zeile class="u-mar-t-2" v-if="zeigeVersichertePerson">
      <titel-zelle v-bind="propsTitelZelle">
        Versich&shy;er&shy;te Per&shy;sonen&nbsp;
        <a role="button" tabindex="0" class="u-copy-s field-edit" @click="geheZuBasisdaten">bearbeiten</a>
      </titel-zelle>
      <zelle v-bind="propsZelle" data-cy="versichertePersonen">
        <strong>{{ getVersichertePersonen }}</strong>
        {{
          piniaStore.state.tarifinformationen.tariff60Plus
            ? " (Versicherungsnehmer ab 60 Jahren werden in diesem Tarifrechner automatisch dem günstigen 60+ Tarif zugeordnet.)"
            : ""
        }}
      </zelle>
    </zeile>

    <zeile class="u-mar-t-2" v-if="zeigeVersichertesObjekt">
      <titel-zelle v-bind="propsTitelZelle">
        Versichertes Objekt
        <a
          role="button"
          tabindex="0"
          class="u-copy-s field-edit"
          @click="geheZuBasisdaten"
          @keydown.enter="geheZuBasisdaten"
          @keydown.space.prevent="geheZuBasisdaten"
          aria-label="Versichertes Objekt bearbeiten"
          >bearbeiten</a
        >
      </titel-zelle>
      <zelle v-bind="propsZelle" data-cy="versichertesObjekt">
        <slot name="versichertes-objekt"></slot>
      </zelle>
    </zeile>

    <zeile class="u-mar-t-2" v-if="zeigeObjektAnschrift">
      <titel-zelle v-bind="propsTitelZelle">
        Objekt-Anschrift
        <a
          role="button"
          tabindex="0"
          class="u-copy-s field-edit"
          @click="geheZuAntragsdaten"
          @keydown.enter="geheZuAntragsdaten"
          @keydown.space.prevent="geheZuAntragsdaten"
          aria-label="Objektanschrift bearbeiten"
          >bearbeiten</a
        >
      </titel-zelle>
      <zelle v-bind="propsZelle" data-cy="objektAnschrift">
        <strong>
          {{ piniaStore.state.risikoAnschrift.plz }}
          {{ piniaStore.state.risikoAnschrift.ort }},
          {{ piniaStore.state.risikoAnschrift.strasse }}
          {{ piniaStore.state.risikoAnschrift.hausnummer }}
        </strong>
      </zelle>
    </zeile>

    <zeile class="u-mar-t-2" v-if="zeigeEigentumsverhaeltnisse">
      <titel-zelle v-bind="propsTitelZelle">
        Eigentumsverhältnisse
        <a
          role="button"
          tabindex="0"
          class="u-copy-s field-edit"
          @click="geheZuAntragsdaten"
          @keydown.enter="geheZuAntragsdaten"
          @keydown.space.prevent="geheZuAntragsdaten"
          aria-label="Eigentumsverhältnisse bearbeiten"
          >bearbeiten</a
        >
      </titel-zelle>
      <zelle v-bind="propsZelle" data-cy="eigentumsverhaeltnisse">
        <strong>{{ eigentumsverhaeltnisse }}</strong>
      </zelle>
    </zeile>

    <zeile class="u-mar-t-2" v-if="piniaStore.state.tarifinformationen.deckungssumme">
      <titel-zelle v-bind="propsTitelZelle">
        Deckungssumme
        <a
          role="button"
          tabindex="0"
          class="u-copy-s field-edit"
          @click="geheZuVergleich"
          @keydown.enter="geheZuVergleich"
          @keydown.space.prevent="geheZuVergleich"
          aria-label="Deckungssumme bearbeiten"
        >
          bearbeiten
        </a>
      </titel-zelle>
      <zelle v-bind="propsZelle" data-cy="deckungssumme">
        <strong>{{ getDeckungssumme }}</strong>
      </zelle>
    </zeile>

    <zeile class="u-mar-t-2" v-if="zeigeAnzahlVorschaeden">
      <titel-zelle v-bind="propsTitelZelle">
        Anzahl Vorschäden
        <a
          role="button"
          tabindex="0"
          class="u-copy-s field-edit"
          @click="geheZuAntragsdaten"
          @keydown.enter="geheZuAntragsdaten"
          @keydown.space.prevent="geheZuAntragsdaten"
          aria-label="Anzahl Vorschäden bearbeiten"
          >bearbeiten</a
        >
      </titel-zelle>
      <zelle v-bind="propsZelle" data-cy="anzahlVorschaeden">
        <strong>{{ vorschaedenAnzahl }}</strong>
      </zelle>
    </zeile>

    <zeile class="u-mar-t-2" v-if="zeigeTarif">
      <titel-zelle v-bind="propsTitelZelle">
        {{ piniaStore.state.isMakler === true ? "" : "Ihr " }}Tarif&nbsp;
        <a
          role="button"
          tabindex="0"
          class="u-copy-s field-edit"
          @click="geheZuVergleich"
          @keydown.enter="geheZuVergleich"
          @keydown.space.prevent="geheZuVergleich"
          aria-label="Tarif bearbeiten"
          >bearbeiten</a
        >
      </titel-zelle>
      <zelle v-bind="propsZelle" data-cy="tarifBezeichnung">
        <strong>{{ getTarifbezeichnung }}</strong>
        <slot name="zusatzmodule"></slot>
      </zelle>
    </zeile>

    <zeile v-if="getSchadenfreiheitsrabatt" class="u-mar-t-2">
      <titel-zelle v-bind="propsTitelZelle">
        Schadenfreiheitsrabatt&nbsp;
        <a
          role="button"
          tabindex="0"
          class="u-copy-s field-edit"
          @click="geheZuVergleich"
          @keydown.enter="geheZuVergleich"
          @keydown.space.prevent="geheZuVergleich"
          aria-label="Schadenfreiheitsrabatt bearbeiten"
          >bearbeiten</a
        >
      </titel-zelle>
      <zelle v-bind="propsZelle" data-cy="schadenfreiheitsrabatt">
        <strong>{{ getSchadenfreiheitsrabatt }}</strong>
      </zelle>
    </zeile>

    <slot name="info-1"></slot>

    <zeile class="u-mar-t-2" v-if="zeigeSelbstbeteiligung">
      <titel-zelle v-bind="propsTitelZelle">
        Selbst&shy;be&shy;teili&shy;gung je Scha&shy;den&nbsp;
        <a
          role="button"
          tabindex="0"
          class="u-copy-s field-edit"
          @click="geheZuVergleich"
          @keydown.enter="geheZuVergleich"
          @keydown.space.prevent="geheZuVergleich"
          aria-label="Selbstbeteiligung bearbeiten"
          >bearbeiten</a
        >
      </titel-zelle>
      <zelle v-bind="propsZelle" data-cy="selbstbeteiligung">
        <strong>{{ getSelbstbeteiligung }}</strong>
      </zelle>
    </zeile>

    <zeile class="u-mar-t-2" v-if="props.obligatorischeSelbstbeteiligung">
      <titel-zelle v-bind="propsTitelZelle"> Selbst&shy;be&shy;teili&shy;gung je Scha&shy;den</titel-zelle>
      <zelle v-bind="propsZelle" data-cy="obligatorische-selbstbeteiligung">
        <strong>{{ props.obligatorischeSelbstbeteiligung }} €</strong>
      </zelle>
    </zeile>

    <slot name="zusaetzliche-selbstbeteiligung"></slot>

    <zeile class="u-mar-t-2" v-if="zusaetzlicheSbLw">
      <titel-zelle v-bind="propsTitelZelle">
        Zu&shy;sätz&shy;liche Selbst&shy;be&shy;teili&shy;gung je Leitungs&shy;wasser-Scha&shy;den&nbsp;
      </titel-zelle>
      <zelle v-bind="propsZelle" data-cy="zusaetzlicheSbLw">
        <strong>750 €</strong>
      </zelle>
    </zeile>

    <zeile class="u-mar-t-2" v-if="zeigeDateien">
      <titel-zelle v-bind="propsTitelZelle">
        Hochgeladene Dateien
        <a
          role="button"
          tabindex="0"
          v-if="!piniaStore.state.preise.produktLinien[0].einmalig"
          class="u-copy-s field-edit"
          @click="geheZuAntragsdaten"
          @keydown.enter="geheZuAntragsdaten"
          @keydown.space.prevent="geheZuAntragsdaten"
          aria-label="Hochgeladene Dateien bearbeiten"
        >
          bearbeiten
        </a>
      </titel-zelle>
      <zelle v-bind="propsZelle" data-cy="dateien">
        <strong>
          <datei-upload-liste ref="dateiuploadliste" />
        </strong>
      </zelle>
    </zeile>

    <zeile class="u-mar-t-2">
      <titel-zelle v-bind="propsTitelZelle">
        Zahlungs&shy;weise&nbsp;
        <a
          role="button"
          tabindex="0"
          v-if="!piniaStore.state.preise.produktLinien[0].einmalig"
          class="u-copy-s field-edit"
          @click="geheZuAntragsdaten"
          @keydown.enter="geheZuAntragsdaten"
          @keydown.space.prevent="geheZuAntragsdaten"
          aria-label="Zahlungsweise bearbeiten"
        >
          bearbeiten
        </a>
      </titel-zelle>
      <zelle v-bind="propsZelle" data-cy="zahlweise">
        <strong>{{ getZahlweiseText }}</strong>
      </zelle>
    </zeile>

    <zeile class="u-mar-t-2">
      <titel-zelle v-bind="propsTitelZelle">
        Zahlungs&shy;art&nbsp;
        <a
          role="button"
          tabindex="0"
          class="u-copy-s field-edit"
          @click="geheZuAntragsdaten"
          @keydown.enter="geheZuAntragsdaten"
          @keydown.space.prevent="geheZuAntragsdaten"
          aria-label="Zahlungsart bearbeiten"
          >bearbeiten</a
        >
      </titel-zelle>
      <zelle v-bind="propsZelle" data-cy="zahlart">
        <strong>{{ getZahlartText }}</strong>
      </zelle>
    </zeile>

    <zeile v-if="getBankverbindung" class="u-mar-t-2">
      <titel-zelle v-bind="propsTitelZelle">
        IBAN&nbsp;
        <a
          role="button"
          tabindex="0"
          class="u-copy-s field-edit"
          @click="geheZuAntragsdaten"
          @keydown.enter="geheZuAntragsdaten"
          @keydown.space.prevent="geheZuAntragsdaten"
          aria-label="Bankdaten bearbeiten"
        >
          bearbeiten
        </a>
      </titel-zelle>
      <zelle v-bind="propsZelle" data-cy="iban">
        <strong>{{ getBankverbindung }} </strong>
      </zelle>
    </zeile>

    <zeile class="u-mar-t-2">
      <titel-zelle
        v-bind="propsTitelZelle"
        :zeige-info="!piniaStore.state.isMakler"
        info-box-id="beitragshilfe"
        class="summary-label"
      >
        {{ piniaStore.state.isMakler === true ? "" : "Ihr " }}Bei&shy;trag inkl. Steuer
      </titel-zelle>
      <zelle
        v-if="!istFhkVerkaufsprodukt(piniaStore.state)"
        v-bind="propsZelle"
        class="summary-price"
        data-cy="beitrag"
      >
        {{
          piniaStore.state.preise.produktLinien[0].einmalig
            ? formatCurrency(piniaStore.state.preise.produktLinien[0].einmalig)
            : formatCurrency(
                piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinienIndex].gemZahlungsweise,
              )
        }}
        <Infotext
          role="tooltip"
          id="beitragshilfe"
          name="beitragshilfe"
          text="Versicherungsbeitrag inklusive Versicherungsteuer."
        ></Infotext>
      </zelle>
      <zelle v-else v-bind="propsZelle" class="summary-price" data-cy="beitrag">
        {{ formatCurrency(gesamtPreisFH) }}
        <Infotext
          role="tooltip"
          id="beitragshilfe"
          name="beitragshilfe"
          text="Versicherungsbeitrag inklusive Versicherungsteuer."
        ></Infotext>
      </zelle>
    </zeile>

    <abschluss-hinweise v-bind:produkt="props.produkt" v-bind:zusaetzlicheSbLw="props.zusaetzlicheSbLw" />

    <abschluss-ihre-daten v-bind:produkt="props.produkt" @gehe-zu-antragsdaten="geheZuAntragsdaten" />
    <ueberschrift
      class="u-mar-t-2"
      :breite="12"
      :titel="
        (piniaStore.state.isMakler ? '' : 'Ihre ') + getAntragOderVertrag(true) + 'sunterlagen und Schlusserklärung'
      "
      :ausrichtung-responsiv="false"
      :vertikal-zentrieren="false"
    />

    <abschluss-makler
      @antrag-absenden="antragAbsenden()"
      :produkt="produkt"
      :fehler="fehler"
      v-bind:zeigeAngabenBestaetigen="zeigeAngabenBestaetigen"
    >
      <template v-slot:gewerbe-zulaessig>
        <slot name="gewerbe-zulaessig" />
      </template>

      <template v-slot:angaben-bestaetigen>
        <slot name="angaben-bestaetigen" />
      </template>

      <template v-slot:schlusserklaerungMakler2>
        <slot name="schlusserklaerungMakler2" />
      </template>
      <template v-slot:button-3>
        <slot name="button-3" />
      </template>
    </abschluss-makler>

    <abschluss-direkt
      @antrag-absenden="antragAbsenden()"
      @vorschlag-herunterladen="vorschlagHerunterladen()"
      :fehler="fehler"
      :produkt="produkt"
    >
      <template v-slot:angaben-bestaetigen>
        <slot name="angaben-bestaetigen" />
      </template>
    </abschluss-direkt>
    <validierungs-fehler v-if="!sindVorherigeStepsValide()" :fehler-text="fehler.vorherigeSteps" />
  </section>

  <modal
    id="abschlussfehler"
    v-if="istSichtbar('abschlussfehler')"
    :hatSchliessenButton="true"
    titel="Fehler beim Abschluss."
    schliessenButtonText="Ok"
    text="Ihr Antrag konnte leider nicht eingereicht werden. Bitte versuchen Sie es erneut."
    @closeModal="wechsleSichtbarkeit('abschlussfehler')"
    @keydown.esc="wechsleSichtbarkeit('abschlussfehler')"
  />

  <modal
    v-if="istSichtbar('modal-ferienhausDownload')"
    :titel="getAntragOderVertrag(true) + ' herunterladen'"
    text="Download-Links:"
    @closeModal="wechsleSichtbarkeit('modal-ferienhausDownload')"
    @keydown.esc="wechsleSichtbarkeit('modal-ferienhausDownload')"
  >
    <template #inhalt="inhaltProps">
      <buendel-dokument-links
        :antrag="true"
        :trackingZusatz="trackingZusatz"
        @textVerbergen="inhaltProps.textVerbergenToggle"
      />
    </template>
  </modal>
</template>
